import React, { createContext, useCallback, useContext, useEffect, useId, useMemo, useRef, useState } from "react";
import { ConfirmDialog } from "../components/ConfirmDialog";
import { getActiveTenantId } from "../lib/activeTenant";

/**
 * ENG-3352 — unsaved edits, and the navigations that would drop them.
 *
 * Policy → Sites & categories holds its edits in local state until Save, and the console unmounts a
 * page (and Policy a tab) the moment you move away. A tab or scope switch, the editors' own links to
 * other tabs, the Related rail, the sidebar and an org switch all threw those edits away without a
 * word, and closing the browser tab did too.
 *
 * An editor reports whether it holds unsaved changes with `useUnsavedChanges(dirty)`. Whatever
 * navigates wraps the move in the guard from `useNavigationGuard()`: with nothing dirty the move runs
 * at once; otherwise the admin is asked first, and Stay keeps the edits while Discard changes goes
 * ahead. While anything is dirty, a `beforeunload` handler covers closing or reloading the tab.
 *
 * The provider wraps DashboardLayout, so any page can register; only Policy's two Sites editors do
 * today. Outside a provider both hooks are inert, which is how the editors render in their own tests.
 *
 * ENG-3376: browser Back/Forward, a trackpad swipe and a hash edit ask too, through
 * `guardUrlNavigation`. The browser has already moved by the time anything hears of those, so the
 * guard steps history back to the page being edited before it asks.
 */

type Guard = (proceed: () => void) => void;

/**
 * ENG-3376 — a page change the browser has already made: Back/Forward, a trackpad swipe, a hash edit.
 *
 * App.tsx turns those into page changes in its popstate/hashchange listener, which sits above this
 * provider, so each mounted provider registers its answer here instead of handing it down. With
 * nothing dirty, or no provider mounted (signed out), `apply` runs at once. Otherwise `undo` steps
 * history back to the page on screen, which leaves every entry where it was, and the admin is asked.
 * Stay leaves it there. Discard changes runs `redo`, the same step again, and that step's own popstate
 * comes back through here with nothing dirty, so it goes ahead.
 */
export interface UrlNavigation {
  /** Show the page the URL now names. */
  apply: () => void;
  /** Step history back to the entry of the page on screen. */
  undo: () => void;
  /** Take the refused step again. */
  redo: () => void;
}

type UrlGuard = (navigation: UrlNavigation) => void;

// The provider mounted last answers. One that unmounts must come off, or it would go on answering for
// a page that is gone and leave the one still mounted, dirty or not, unasked.
const urlGuards: UrlGuard[] = [];

export function guardUrlNavigation(navigation: UrlNavigation): void {
  const guard = urlGuards[urlGuards.length - 1];
  if (guard) guard(navigation);
  else navigation.apply();
}

interface UnsavedChangesApi {
  setDirty: (id: string, dirty: boolean) => void;
  guard: Guard;
}

const UnsavedChangesContext = createContext<UnsavedChangesApi | null>(null);

const runNow: Guard = (proceed) => proceed();

export const UnsavedChangesProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  // The editors holding unsaved changes. A ref, so the guard and the unload handler read it as it is
  // when they run, not as of the last render.
  const dirtyIds = useRef(new Set<string>());
  const [anyDirty, setAnyDirty] = useState(false);
  // The navigation waiting on an answer. Wrapped in an object because state cannot hold a bare function.
  const [pending, setPending] = useState<{ proceed: () => void } | null>(null);
  // The organization selection this page loaded with (see the unload handler below).
  const [tenantAtMount] = useState(getActiveTenantId);

  const setDirty = useCallback((id: string, dirty: boolean) => {
    if (dirty) dirtyIds.current.add(id);
    else dirtyIds.current.delete(id);
    setAnyDirty(dirtyIds.current.size > 0);
  }, []);

  const guard = useCallback<Guard>((proceed) => {
    if (dirtyIds.current.size === 0) proceed();
    else setPending({ proceed });
  }, []);

  // ENG-3376: see `guardUrlNavigation`.
  useEffect(() => {
    const guardUrl: UrlGuard = ({ apply, undo, redo }) => {
      if (dirtyIds.current.size === 0) {
        apply();
        return;
      }
      undo();
      setPending({ proceed: redo });
    };
    urlGuards.push(guardUrl);
    return () => {
      const at = urlGuards.lastIndexOf(guardUrl);
      if (at !== -1) urlGuards.splice(at, 1);
    };
  }, []);

  // Registered only while something is dirty. The handler still checks the ref when it fires: an org
  // switch reloads the page straight after Discard, before this effect has had a chance to clean up,
  // and that reload must not ask a second time.
  //
  // Nor may it hold a reload that follows a change of organization it did not ask about — App.tsx
  // reloads when ANOTHER tab switches (ENG-2573). The selection is already changed by then, so a
  // Cancel would leave this tab showing one organization while its requests, a Save included, went
  // to the other.
  useEffect(() => {
    if (!anyDirty) return;
    const onBeforeUnload = (e: BeforeUnloadEvent) => {
      if (dirtyIds.current.size === 0 || getActiveTenantId() !== tenantAtMount) return;
      e.preventDefault();
      e.returnValue = ""; // older Chromium shows the prompt only when this is set
    };
    window.addEventListener("beforeunload", onBeforeUnload);
    return () => window.removeEventListener("beforeunload", onBeforeUnload);
  }, [anyDirty, tenantAtMount]);

  const stay = () => setPending(null);
  const discard = () => {
    const next = pending;
    setPending(null);
    // Every navigation guarded here drops the editors' state, by unmounting them or by reloading the
    // page, so nothing is left dirty once it has run. An editor registers again the next time its
    // own `dirty` turns true. (A Back/Forward runs a moment later, on the popstate that `redo`
    // raises, and must find nothing registered by then, or it would ask again.)
    dirtyIds.current.clear();
    setAnyDirty(false);
    next?.proceed();
  };

  const api = useMemo(() => ({ setDirty, guard }), [setDirty, guard]);

  return (
    <UnsavedChangesContext.Provider value={api}>
      {children}
      {/* ENG-3351: the console's shared ConfirmDialog. Stay is focused, so Enter and Escape both keep
          the edits. */}
      <ConfirmDialog
        open={pending !== null}
        title="Discard unsaved changes?"
        confirmLabel="Discard changes"
        danger
        cancelLabel="Stay"
        focusCancel
        maxWidth="xs"
        onCancel={stay}
        onConfirm={discard}
      >
        You have changes that haven&rsquo;t been saved. If you leave now, they&rsquo;ll be lost.
      </ConfirmDialog>
    </UnsavedChangesContext.Provider>
  );
};

/** Tells the nearest provider whether this component holds changes that a navigation would drop. */
export function useUnsavedChanges(dirty: boolean): void {
  const api = useContext(UnsavedChangesContext);
  const id = useId();
  useEffect(() => {
    if (!api || !dirty) return;
    api.setDirty(id, true);
    return () => api.setDirty(id, false);
  }, [api, id, dirty]);
}

/**
 * Wrap a navigation in this: it runs at once when nothing is dirty, and after a Discard changes
 * otherwise. Outside a provider it always runs at once.
 */
export function useNavigationGuard(): Guard {
  return useContext(UnsavedChangesContext)?.guard ?? runNow;
}
